/**
 * ProductTilesPickerModal — Screen 23 of the SPEC's 3-modal product flow.
 *
 * Lists every active product in the picked category as a tile. Tiles use
 * the canonical PCB category icon (NOT a per-product cutesy art — Bill
 * 2026-05-13 direction: category icon + prominent product titling for a
 * professional rollout). Ineligible products render greyed with a tooltip
 * surfacing the reason; clicking an eligible tile calls onPick with the
 * product code.
 *
 * Eligibility runs the SAME rule as the add modal — businessOnly / personalOnly against
 * the cart's applicant types, AND ineligibilityReason (age + call-class) to confirm at
 * least one cart person can be the primary owner. Bill + Peter 2026-07-16: the tile used
 * to check only customer type, so it offered Minor Student Checking to a cart of adults
 * and then the modal blocked with "no cart persons qualified as Primary." Two rules, two
 * answers. The picker still greys rather than hides (SPEC Screen 23); it just no longer
 * greys for a reason the FSR can't see or offers a product nobody can own.
 *
 * Sources:
 *  - docs/mockups/canonical-walkthrough/SPEC.md Screen 23 (line 2156)
 *  - feedback_admin_configurable_with_legacy_default_20260511.md — SortOrder
 *    is admin-editable; we sort ascending by sortOrder, then by name as
 *    secondary key. Featured products typically have low sortOrder.
 */
import { useMemo, useState } from 'react';
import type { Product } from '@retail-ops/shared-types';
import { ModalShell, Button, Pill } from '@/components/primitives';
import { productCategoryIconUrl } from '@/icons/flat-library';
import { useConfig } from '@/contexts/ConfigContext';
import type { ProductCategoryKey } from './ProductCategoryPickerModal';
// The tile picker and the add modal MUST share one eligibility rule — running two rules is
// exactly what let a product tile offer something no cart person could own (Bill 2026-07-16).
import { ineligibilityReason, type CartPersonForModal } from './AddToCartModal';
import { rolloutHidesDisabled, SETTING_DISABLED_DISPLAY, SETTING_ENABLED_ENTITY_GROUPS } from '@/utils/rolloutScope';
import { enabledEntityTypes, productInRolloutScope } from '../rolloutProducts';

export interface ProductTilesPickerModalProps {
  open: boolean;
  /** Selected category from the CategoryPickerModal. */
  categoryKey: ProductCategoryKey;
  /** Applicant types present on the cart — drives eligibility greying. */
  businessTypes: ReadonlySet<string>;
  personalTypes: ReadonlySet<string>;
  /** Cart's people customer types (used to evaluate eligibility). */
  applicantCustomerTypes: ReadonlyArray<string>;
  /** Cart's people with DOB — so the tile can run the SAME age/primary check the add modal
   *  runs, instead of offering a product no one can own (Bill 2026-07-16). */
  cartPeople: ReadonlyArray<CartPersonForModal>;
  onClose: () => void;
  /** Called when FSR clicks Continue on an eligible product. */
  onPick: (productCode: string) => void;
  /** Called when FSR clicks ← Back — re-opens the category picker. */
  onReturn: () => void;
}

const CATEGORY_DISPLAY: Record<ProductCategoryKey, { title: string; iconSlug: string }> = {
  checking:     { title: 'Checking',         iconSlug: 'dda' },
  savings:      { title: 'Savings',          iconSlug: 'sav' },
  certificates: { title: 'Certificates (CD)', iconSlug: 'cd' },
  iras:         { title: 'IRAs',             iconSlug: 'ira' },
  sdb:          { title: 'Safe Deposit Box', iconSlug: 'sdb' },
};

const ACCT_TYPE_FOR_CATEGORY: Record<ProductCategoryKey, string> = {
  checking:     '10',
  savings:      '20',
  certificates: '30',
  iras:         '60',
  sdb:          '50',
};

interface TileRow {
  readonly product: Product;
  readonly isEligible: boolean;
  readonly ineligibleReason?: string;
}

function computeEligibility(
  product: Product,
  applicantCustomerTypes: ReadonlyArray<string>,
  businessTypes: ReadonlySet<string>,
  personalTypes: ReadonlySet<string>,
  cartPeople: ReadonlyArray<CartPersonForModal>,
): { isEligible: boolean; reason?: string } {
  if (applicantCustomerTypes.length === 0) {
    return {
      isEligible: false,
      reason: 'Add at least one applicant on the People step before picking a product.',
    };
  }
  if (product.businessOnly) {
    const hasBiz = applicantCustomerTypes.some((t) => businessTypes.has(t));
    if (!hasBiz) {
      return {
        isEligible: false,
        reason: 'Requires a business entity on the cart (Corp, LLC, DBA, Partnership, etc.).',
      };
    }
  }
  if (product.personalOnly) {
    const hasPersonal = applicantCustomerTypes.some((t) => personalTypes.has(t));
    if (!hasPersonal) {
      return {
        isEligible: false,
        reason: 'Requires a personal applicant (Individual or Minor) on the cart.',
      };
    }
  }

  // Can ANYONE on the cart actually be the primary owner? (Bill + Peter, 2026-07-16.)
  //
  // The tile picker used to gate only on customer TYPE (business/personal), so a Minor
  // Student Checking tile showed as available whenever a personal applicant was on the
  // cart — then the add modal found that nobody qualified as primary and blocked with
  // "no cart persons qualified as Primary. That's BS." It wasn't BS: STUDEN_MINOR requires
  // a primary aged 12–17, and the cart held two adults and an under-12. The gates
  // DISAGREED — the tile said yes, the modal said no — because they ran different rules.
  //
  // Now they run the SAME rule: ineligibilityReason (the age/call-class check the modal
  // itself uses). If no cart person can be primary, the tile is greyed with the reason,
  // and a person's specific block (e.g. "Age 8 — requires 12+") is surfaced verbatim so
  // the FSR knows WHO and WHY, not just "unavailable".
  const primaryReasons = cartPeople.map((p) => ineligibilityReason(p, product, 'primary'));
  const anyEligiblePrimary = primaryReasons.some((r) => r === null);
  if (cartPeople.length > 0 && !anyEligiblePrimary) {
    // Say what the product NEEDS, not why one arbitrary person fails. "requires an owner
    // aged 12–17" is actionable; "age 40 — max age 17" (the first adult tested) reads like
    // a complaint about the wrong person. Derive the window straight from the product so
    // the FSR reads the requirement, not a rejection.
    const minP = product.minAgePrimary ?? product.minAge ?? null;
    const maxP = product.maxAgePrimary ?? product.maxAge ?? null;
    let need: string;
    if (minP !== null && maxP !== null) need = `an owner aged ${minP}–${maxP}`;
    else if (minP !== null) need = `an owner at least ${minP}`;
    else if (maxP !== null) need = `an owner ${maxP} or younger`;
    else {
      // No age window → a call-class/type block, not age. Surface the concrete reason.
      const firstReason = primaryReasons.find((r): r is string => !!r);
      need = firstReason
        ? `a different owner (${firstReason.charAt(0).toLowerCase()}${firstReason.slice(1)})`
        : 'an eligible primary owner';
    }
    return {
      isEligible: false,
      reason: `No one on the cart can be the primary owner — this product needs ${need}. Add one on the People step.`,
    };
  }

  return { isEligible: true };
}

export function ProductTilesPickerModal({
  open,
  categoryKey,
  businessTypes,
  personalTypes,
  applicantCustomerTypes,
  cartPeople,
  onClose,
  onPick,
  onReturn,
}: ProductTilesPickerModalProps): JSX.Element {
  const { config } = useConfig();
  const [pickedCode, setPickedCode] = useState<string | null>(null);

  const display = CATEGORY_DISPLAY[categoryKey];
  const targetAcctType = ACCT_TYPE_FOR_CATEGORY[categoryKey];
  const iconUrl = productCategoryIconUrl(display.iconSlug);

  // Build the rows for this category — active products only, sorted by
  // (sortOrder ascending, name ascending). Eligibility computed per row.
  const rows: ReadonlyArray<TileRow> = useMemo(() => {
    // Phase rollout scope (Bill 2026-10-06): products no enabled entity category can own
    // are hidden, or greyed "coming soon", per rollout.disabledDisplay.
    const settings = config.systemSettings as Record<string, unknown> | undefined;
    const enabledTypes = enabledEntityTypes(settings?.[SETTING_ENABLED_ENTITY_GROUPS]);
    const hideOut = rolloutHidesDisabled(settings?.[SETTING_DISABLED_DISPLAY]);
    const list = (config.products ?? [])
      .filter((p) => p.isActive && p.acctType === targetAcctType)
      .filter((p) => !hideOut || productInRolloutScope(p, enabledTypes))
      .slice()
      .sort((a, b) => {
        const ao = (a as Product & { sortOrder?: number }).sortOrder ?? 999;
        const bo = (b as Product & { sortOrder?: number }).sortOrder ?? 999;
        if (ao !== bo) return ao - bo;
        return a.name.localeCompare(b.name);
      });
    return list.map((product) => {
      if (!productInRolloutScope(product, enabledTypes)) {
        return { product, isEligible: false, ineligibleReason: 'Not offered yet — coming soon.' };
      }
      const { isEligible, reason } = computeEligibility(
        product,
        applicantCustomerTypes,
        businessTypes,
        personalTypes,
        cartPeople,
      );
      return reason
        ? { product, isEligible, ineligibleReason: reason }
        : { product, isEligible };
    });
  }, [config.products, config.systemSettings, targetAcctType, applicantCustomerTypes, businessTypes, personalTypes, cartPeople]);

  const eligibleCount = rows.filter((r) => r.isEligible).length;
  const greyedCount = rows.length - eligibleCount;

  const onTileClick = (row: TileRow): void => {
    if (!row.isEligible) return;
    setPickedCode(row.product.code);
    onPick(row.product.code);  // auto-advance to AddToCartModal — no Continue step
  };

  return (
    <ModalShell
      open={open}
      onClose={onClose}
      title={`Add product · Choose a ${display.title} account`}
      size="lg"
      footer={
        <Button variant="ghost" onClick={onReturn}>← Back</Button>
      }
    >
      <div className="rao-prod-picker">
        <header className="rao-prod-picker__header">
          <div className="rao-prod-picker__cat-icon">
            {iconUrl && <img src={iconUrl} alt="" aria-hidden width={48} height={48} />}
          </div>
          <div>
            <h3 className="rao-prod-picker__title">Which {display.title} product?</h3>
            <p className="rao-prod-picker__sub">
              Sorted by admin-config priority. Ineligible products are greyed —
              hover for the reason.
            </p>
          </div>
        </header>

        <div className="rao-prod-picker__grid">
          {rows.map((row) => {
            const isPicked = pickedCode === row.product.code;
            const sortOrder = (row.product as Product & { sortOrder?: number }).sortOrder;
            return (
              <button
                key={row.product.code}
                type="button"
                className={`rao-prod-picker__tile ${row.isEligible ? '' : 'is-greyed'} ${isPicked ? 'is-picked' : ''}`.trim()}
                aria-pressed={isPicked ? 'true' : 'false'}
                aria-disabled={!row.isEligible}
                title={row.ineligibleReason}
                onClick={() => onTileClick(row)}
              >
                <div className="rao-prod-picker__tile-icon">
                  {iconUrl && <img src={iconUrl} alt="" aria-hidden width={36} height={36} />}
                </div>
                <div className="rao-prod-picker__tile-name">{row.product.name}</div>
                <div className="rao-prod-picker__tile-code">{row.product.code}</div>
                {row.product.description && (
                  <div className="rao-prod-picker__tile-desc">{row.product.description}</div>
                )}
                {!row.isEligible && row.ineligibleReason && (
                  <div className="rao-prod-picker__tile-warn">
                    Not eligible: {row.ineligibleReason}
                  </div>
                )}
                {sortOrder !== undefined && sortOrder <= 20 && row.isEligible && (
                  <div className="rao-prod-picker__tile-featured">
                    <Pill variant="info">Featured</Pill>
                  </div>
                )}
              </button>
            );
          })}
        </div>

        {rows.length === 0 && (
          <p className="rao-prod-picker__empty">
            No products available in this category for the current cart.
          </p>
        )}

        <p className="rao-prod-picker__footer">
          {eligibleCount} of {rows.length} eligible
          {greyedCount > 0 ? ` · ${greyedCount} greyed (see tooltip for reason)` : ''}
          {' · admin-config sort order applies.'}
        </p>
      </div>
    </ModalShell>
  );
}
